import type { ReactNode } from "react";

/**
 * Keep SSR HTML visible without hydrating the subtree (no Suspense / `use()`).
 *
 * The old DeferredHydration gate threw React #482 during Builder `<Content>`
 * hydration. This island never hydrates `<Content>`: the first client render
 * snapshots the still-present SSR markup and paints it via
 * `dangerouslySetInnerHTML`. After `ready` (fetch of the page tree) the live
 * React tree replaces the snapshot.
 *
 * Native `<a>` tags in the snapshot keep working the whole time.
 *
 * ## The id MUST be deterministic — this was a 966ms blank homepage
 *
 * The snapshot is a DOM lookup across the hydration boundary, so the id the
 * client computes has to equal the one the server rendered. This component
 * originally derived it from `useId()`, and `useId()` DOES NOT AGREE across
 * that boundary here — measured on develop at 4x CPU throttle:
 *
 *   server rendered   id="ssr-island-_R_agv3ln5_"
 *   client computed   id="ssr-island-_r_m_"
 *
 * So `getElementById` missed, `snap` was `""`, and the island painted an empty
 * div with its own `min-h-[70vh]` fallback. Sampling the island every 100ms:
 *
 *   t=3,810ms  innerHTML 838,906  imgs 177          (SSR markup, correct)
 *   t=6,908ms  innerHTML       0  imgs   0  <- hydration wiped it
 *   t=8,336ms  innerHTML 840,066  imgs 177          (tree fetch landed)
 *
 * ~966ms of blank page body AFTER it had already painted. It did not show up
 * in CLS because COM-187's viewport-height reservation held the space, so the
 * content vanished without shifting anything — the metric stayed at 0.002 while
 * the defect was fully present. It was found by eye, in a Lighthouse filmstrip.
 *
 * `useId` is also tree-POSITION sensitive: adding an unrelated sibling element
 * to the route changed the server id from `_R_agv3ln5_` to `_R_2agv3ln5_`. Even
 * if it did agree across hydration, it would be a fragile handle for a
 * cross-boundary DOM lookup. The caller passes a stable key instead.
 *
 * (The removed `.replace(/:/g, "")` was also dead: it stripped React 18's
 * `:r0:` delimiters, and React 19 emits `_R_..._`.)
 */
export function CrashSafeSsrIsland({
  children,
  upgrade,
  ready,
  islandKey,
}: {
  children: ReactNode;
  upgrade: ReactNode;
  ready: boolean;
  /**
   * Identifier for this island. Two requirements, and BOTH are load-bearing:
   *
   *  1. IDENTICAL on server and client for the same page. The snapshot is a
   *     `getElementById` across the hydration boundary, so a mismatch means an
   *     empty snapshot and a blank body. Do NOT derive it from `useId()`, a
   *     render counter, or anything position-dependent — `useId()` disagrees
   *     across hydration here and caused a ~966ms blank homepage.
   *
   *  2. DIFFERENT per page. On a client-side navigation the OUTGOING page's
   *     island is still mounted when the incoming page first renders. With a
   *     shared key, `getElementById` finds the old island and the new page
   *     snapshots the PREVIOUS page's markup — the URL and <title> update while
   *     the body still shows the old page until a hard refresh. Reproduced on
   *     develop: home -> /patiently/fsa-weight-loss.html changed the title but
   *     left the island byte-identical at 869,320 chars.
   *
   * Use `builderPageIslandKey(path)` rather than composing this by hand.
   */
  islandKey: string;
}) {
  const id = `ssr-island-${islandKey}`;

  if (typeof window === "undefined") {
    return <div id={id}>{children}</div>;
  }

  if (ready) {
    return <div id={id}>{upgrade}</div>;
  }

  const snap = document.getElementById(id)?.innerHTML ?? "";
  return (
    <div
      id={id}
      className={snap ? undefined : "min-h-[70vh]"}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: snap }}
    />
  );
}
